import React from "react";
import { StaticImage } from "gatsby-plugin-image";

import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";

import "../../scss/main.scss";
import "../../scss/_blog.scss";

const title = "Privacy Policy";

const EmailLink = () => <a href="mailto:hello@edyn.care">hello@edyn.care</a>;

const BulletList = ({ items }) => (
	<ul>
		{items.map((item) => (
			<li key={item}>{item}</li>
		))}
	</ul>
);

export default function Privacy() {
	return (
		<>
			<Seo
				title={`${title} | Edyn Care`}
				description="Read how Edyn Care collects, uses, stores and protects personal information, including your rights under UK data protection law."
				path="/privacy/"
			/>
			<div className="container edyn-commercial-shell edyn-article-shell">
				<Header />
				<main className="blog-post">
					<div className="blog-post__above-the-fold">
						<StaticImage
							src="../../static/assets/marketing-site/images/our-approach-01.png"
							objectFit="contain"
							placeholder="blurred"
							loading="eager"
						/>
						<div>
							<div className="privacy-policy__version-badge">Version 2.0</div>
							<h1 className="blog-post__above-the-fold-title">
								Privacy Policy
							</h1>
							<p className="blog-post__above-the-fold-published">
								Last updated: May 2026
							</p>
						</div>
						<StaticImage
							src="../../static/assets/marketing-site/images/our-approach-02.png"
							objectFit="contain"
							placeholder="blurred"
							loading="eager"
						/>
					</div>
					<section className="blog-post-section">
						<div className="blog-post__content privacy-policy__content">
							<p>
								Welcome to the edyn.care website Privacy Policy. Before using
								our website and services, please read this policy carefully. If
								you have any questions relating to this Privacy Policy, please
								contact us at:
							</p>

							<p>
								<strong>Email:</strong> <EmailLink />
								<br />
								<strong>Phone:</strong> 020 3970 9900
							</p>

							<p>
								Age Abode Limited (company number 10713673), trading as{" "}
								<strong>edyn.care</strong> ("edyn.care", "we", "us", or "our"),
								operates the website{" "}
								<a href="http://www.edyn.care">www.edyn.care</a> and provides
								care-related services throughout England.
							</p>

							<p>
								For the purposes of the UK General Data Protection Regulation
								("UK GDPR"), the Data Protection Act 2018, and where applicable
								the Privacy and Electronic Communications Regulations ("PECR"),
								Age Abode Limited is the data controller responsible for your
								personal data.
							</p>

							<p>
								We are committed to protecting your privacy and handling your
								personal data transparently, securely, and lawfully.
							</p>

							<h2>Contact Information</h2>

							<p>
								<strong>Age Abode Limited (trading as edyn.care)</strong>
								<br />
								Shoreditch Exchange
								<br />
								Senna Building
								<br />
								Gorsuch Place
								<br />
								London
								<br />
								E2 8JF
							</p>

							<p>
								Email: <EmailLink />
								<br />
								Phone: 020 3970 9900
							</p>

							<p>
								We have appointed an individual responsible for overseeing data
								protection and privacy matters within the business.
							</p>

							<p>
								If you have any questions regarding this Privacy Policy or your
								data protection rights, please contact us using the details
								above.
							</p>

							<h2>Information We Collect</h2>

							<p>
								In order to provide our services, we may collect and process the
								following categories of personal data.
							</p>

							<h3>For Clients</h3>

							<h4>Personal Information</h4>
							<p>We may collect:</p>
							<BulletList
								items={[
									"name",
									"gender",
									"date of birth",
									"nationality",
									"preferences and interests",
									"details about the home environment",
									"pets",
									"transportation preferences",
									"smoking preferences",
									"emergency contact information",
									"health and care-related information relevant to providing care services",
								]}
							/>

							<h4>Contact Information</h4>
							<p>We may collect:</p>
							<BulletList
								items={[
									"email address",
									"telephone number",
									"residential address",
									"house access instructions",
								]}
							/>

							<h4>Financial Information</h4>
							<p>We may collect:</p>
							<BulletList
								items={[
									"billing information",
									"payment details",
									"bank or card details where necessary for payment processing",
								]}
							/>

							<h4>Care and Health Information</h4>
							<p>Where necessary to provide care services, we may process:</p>
							<BulletList
								items={[
									"medical conditions",
									"medication information",
									"mobility information",
									"care requirements",
									"risk assessments",
									"healthcare professional information",
								]}
							/>
							<p>
								This information is considered special category personal data
								under UK GDPR and is processed only where lawful and necessary.
							</p>

							<h4>Device and Technical Information</h4>
							<p>We may collect:</p>
							<BulletList
								items={[
									"IP address",
									"browser type",
									"device identifiers",
									"operating system",
									"website usage data",
									"cookies and analytics information",
								]}
							/>

							<h3>For Carers and Applicants</h3>

							<h4>Personal Information</h4>
							<p>We may collect:</p>
							<BulletList
								items={[
									"name",
									"gender",
									"date of birth",
									"professional history",
									"qualifications",
									"work preferences",
									"nationality",
									"right to work documentation",
									"emergency contacts",
								]}
							/>

							<h4>Recruitment and Vetting Information</h4>
							<p>We may collect:</p>
							<BulletList
								items={[
									"DBS information",
									"identity verification information",
									"references",
									"employment history",
									"training records",
									"compliance documentation",
								]}
							/>

							<h4>Financial Information</h4>
							<p>We may collect:</p>
							<BulletList
								items={[
									"bank details",
									"payment information",
									"invoice information",
								]}
							/>

							<h4>Device and Usage Information</h4>
							<p>We may collect:</p>
							<BulletList
								items={[
									"location data",
									"check-in/check-out information",
									"device identifiers",
									"app usage information",
								]}
							/>

							<h2>Information Automatically Collected</h2>

							<p>
								When you use our website or services, we may automatically
								collect:
							</p>
							<BulletList
								items={[
									"IP address",
									"browser type",
									"operating system",
									"referring URLs",
									"pages viewed",
									"session duration",
									"device information",
									"cookie information",
								]}
							/>

							<h2>How We Collect Your Data</h2>

							<p>We collect data through:</p>
							<BulletList
								items={[
									"direct interactions",
									"telephone calls",
									"website forms",
									"email correspondence",
									"onboarding processes",
									"third-party providers",
									"automated technologies such as cookies and analytics tools",
								]}
							/>

							<h2>Lawful Basis for Processing</h2>

							<p>We only process personal data where lawful under UK GDPR.</p>
							<p>The lawful bases we rely upon include:</p>
							<BulletList
								items={[
									"performance of a contract",
									"compliance with legal obligations",
									"legitimate interests",
									"consent",
									"protection of vital interests",
								]}
							/>

							<p>
								Where we process special category health data, we may rely on:
							</p>
							<BulletList
								items={[
									"explicit consent",
									"Article 9(2)(h) UK GDPR for the provision of health or social care and the management of health and social care systems and services",
									"safeguarding obligations",
									"employment and social protection law obligations where applicable",
								]}
							/>

							<h2>How We Use Your Information</h2>

							<p>We may use your information to:</p>
							<BulletList
								items={[
									"provide care services",
									"match clients with carers",
									"manage customer relationships",
									"process payments",
									"conduct recruitment and compliance checks",
									"improve our services",
									"maintain platform security",
									"communicate with you",
									"comply with legal obligations",
									"prevent fraud",
									"manage complaints and safeguarding concerns",
								]}
							/>

							<p>
								We may process and disclose personal data where necessary to
								protect individuals from harm, support safeguarding obligations,
								or comply with legal and regulatory duties.
							</p>

							<h2>Artificial Intelligence ("AI") and Automated Tools</h2>

							<p>
								We may use AI-assisted software tools, including services
								provided by OpenAI, to support:
							</p>
							<BulletList
								items={[
									"administrative efficiency",
									"communication drafting",
									"operational processes",
									"recruitment support",
									"internal documentation",
									"service improvement",
								]}
							/>

							<p>
								These systems operate under human oversight and are subject to
								contractual, technical, and organisational safeguards designed
								to protect personal data.
							</p>

							<p>
								We do not use AI systems to make fully automated decisions about
								individuals without meaningful human involvement.
							</p>

							<p>
								AI-assisted systems are not used as a substitute for
								professional judgement, clinical decision-making, safeguarding
								assessments, or regulated care responsibilities.
							</p>

							<p>
								We do not intentionally use identifiable client health data to
								train public AI models.
							</p>

							<p>
								Users and staff are instructed not to input confidential health
								records, safeguarding information, or other sensitive personal
								data into AI systems unless the system has been formally
								approved for that purpose and appropriate safeguards are in
								place.
							</p>

							<h2>Marketing Communications</h2>

							<p>
								Where required by law, we will obtain your consent before
								sending marketing communications.
							</p>
							<p>You may opt out of marketing communications at any time by:</p>
							<BulletList
								items={[
									"clicking the unsubscribe link in emails",
									"contacting us directly",
								]}
							/>

							<p>
								Service-related communications may still be sent where
								necessary.
							</p>

							<h2>Verification Checks</h2>

							<p>We may conduct checks on carers and applicants including:</p>
							<BulletList
								items={[
									"Enhanced DBS checks",
									"identity verification",
									"right to work checks",
									"employment history checks",
									"reference checks",
									"professional qualification verification",
								]}
							/>

							<p>
								These checks may be carried out using approved third-party
								providers.
							</p>

							<p>
								Applicant information may be retained for a reasonable period
								following recruitment processes unless otherwise required by law
								or withdrawn by the applicant.
							</p>

							<h2>Location Data</h2>

							<p>
								Where carers use our platform or mobile applications, we may
								process location data for:
							</p>
							<BulletList
								items={[
									"attendance verification",
									"mileage tracking",
									"operational monitoring",
									"safeguarding purposes",
								]}
							/>

							<p>
								You may disable location permissions on your device; however,
								this may limit certain platform functionality.
							</p>

							<h2>Data Sharing</h2>

							<p>
								We may share personal data with trusted third-party service
								providers where necessary for the operation of our business and
								the delivery of our services.
							</p>

							<p>These may include providers relating to:</p>
							<BulletList
								items={[
									'cloud hosting and infrastructure services, including Amazon Web Services ("AWS")',
									"payment processing and financial services providers, including Stripe",
									"communications, email delivery and customer engagement platforms, including SendGrid and automation providers",
									"analytics and website performance providers, including Google Analytics and related Google services",
									"AI-assisted software and operational tools, including OpenAI",
									"recruitment and job advertising platforms, including Indeed",
									"recruitment and compliance providers",
									"IT support and software providers",
									"professional advisers",
									"regulators, government authorities, law enforcement agencies, and safeguarding bodies where legally required",
								]}
							/>

							<p>We require all third-party providers to:</p>
							<BulletList
								items={[
									"process personal data lawfully and securely",
									"act only on our instructions where applicable",
									"implement appropriate technical and organisational security measures",
									"comply with applicable UK data protection laws",
								]}
							/>

							<p>
								Where personal data is transferred outside the United Kingdom,
								we ensure appropriate safeguards are in place, including
								International Data Transfer Agreements ("IDTAs"), Standard
								Contractual Clauses ("SCCs"), adequacy decisions, or equivalent
								lawful transfer mechanisms.
							</p>

							<p>We do not sell personal data to third parties.</p>

							<h2>International Transfers</h2>

							<p>
								Some third-party providers may process personal data outside the
								United Kingdom.
							</p>

							<p>
								Where international transfers occur, we ensure appropriate
								safeguards are in place, including:
							</p>
							<BulletList
								items={[
									"adequacy regulations",
									'International Data Transfer Agreements ("IDTAs")',
									'Standard Contractual Clauses ("SCCs")',
									"contractual and technical safeguards",
								]}
							/>

							<h2>Data Retention</h2>

							<p>We retain personal data only for as long as necessary for:</p>
							<BulletList
								items={[
									"providing services",
									"legal and regulatory obligations",
									"safeguarding requirements",
									"dispute resolution",
									"accounting and tax obligations",
								]}
							/>

							<p>
								Retention periods may vary depending on the type of data and
								legal requirements.
							</p>

							<h2>Data Security</h2>

							<p>
								We implement appropriate technical and organisational measures
								to protect personal data, including:
							</p>
							<BulletList
								items={[
									"encryption",
									"access controls",
									"secure hosting",
									"staff confidentiality obligations",
									"monitoring and security controls",
									"staff training",
									"breach response procedures",
								]}
							/>

							<p>
								In the event of a personal data breach, we will take appropriate
								steps to investigate, mitigate, and where legally required,
								notify affected individuals and relevant regulators.
							</p>

							<h2>Vulnerable Individuals</h2>

							<p>
								We recognise that some individuals receiving services may be
								vulnerable adults. We take additional care to ensure personal
								data relating to vulnerable individuals is handled
								appropriately, securely, and in accordance with applicable legal
								and safeguarding obligations.
							</p>

							<h2>Change of Business Ownership and Control</h2>

							<p>
								Age Abode Limited may, from time to time, expand or reduce its
								business operations and this may involve the sale, transfer,
								merger, restructuring, or other change of control of all or part
								of the business.
							</p>

							<p>
								In the unlikely event of such a transaction, personal data may
								be disclosed to prospective purchasers, advisers, or
								counterparties under appropriate confidentiality arrangements
								and may be transferred as part of the transaction where lawful
								and necessary.
							</p>

							<p>
								Any such transfer of personal data will continue to be subject
								to this Privacy Policy and applicable UK data protection laws.
							</p>

							<h2>Cookies</h2>

							<p>We use cookies and similar technologies on our website to:</p>
							<BulletList
								items={[
									"ensure the website functions correctly",
									"improve website performance and security",
									"understand how visitors use our website",
									"personalise user experience",
									"support analytics and marketing activities where consent has been provided",
								]}
							/>

							<p>
								We use a cookie consent management platform provided by
								Cookiebot by Usercentrics to manage cookie preferences and
								consent.
							</p>

							<p>
								Non-essential cookies, including analytics, functionality and
								advertising cookies, are only placed on your device where you
								have provided your consent.
							</p>

							<p>
								You can manage or withdraw your cookie preferences at any time
								through our cookie settings tool available on our website.
							</p>

							<h3>What Are Cookies?</h3>

							<p>
								Cookies are small text files stored on your device when you
								visit a website. They help websites function efficiently,
								improve user experience, and provide information about how the
								website is used.
							</p>

							<p>Cookies may be:</p>
							<BulletList
								items={[
									"session cookies, which expire when you close your browser",
									"persistent cookies, which remain on your device until deleted or expired",
								]}
							/>

							<h3>Types of Cookies We Use</h3>

							<h4>Necessary Cookies</h4>
							<p>
								These cookies are essential for the operation, security and core
								functionality of our website. The website cannot function
								properly without these cookies.
							</p>

							<h4>Analytics Cookies</h4>
							<p>
								These cookies help us understand how visitors interact with our
								website by collecting information such as pages visited, time
								spent on the site, and technical performance data. This helps us
								improve the functionality and performance of our website.
							</p>

							<h4>Functionality Cookies</h4>
							<p>
								These cookies allow the website to remember choices you make and
								provide enhanced, more personalised features.
							</p>

							<h4>Marketing and Targeting Cookies</h4>
							<p>
								These cookies may be used to deliver relevant advertising,
								measure marketing effectiveness, and understand browsing
								behaviour across websites.
							</p>

							<p>
								These cookies are only used where you have provided consent.
							</p>

							<h3>Managing Cookies</h3>

							<p>You can:</p>
							<BulletList
								items={[
									"accept or reject non-essential cookies through our cookie banner",
									"change your cookie preferences at any time through our cookie settings tool",
									"manage cookies through your browser settings",
								]}
							/>

							<p>
								Please note that disabling certain cookies may affect website
								functionality and user experience.
							</p>

							<h3>Third-Party Cookies</h3>

							<p>
								Some cookies may be placed by third-party services that appear
								on our website, including analytics and advertising providers.
								These third parties may process information in accordance with
								their own privacy policies.
							</p>

							<h3>Further Information</h3>

							<p>
								Further details about the specific cookies used on our website,
								including cookie duration and providers, are available through
								our cookie consent management platform.
							</p>

							<h2>Your Rights</h2>

							<p>Under UK GDPR, you have rights including:</p>
							<BulletList
								items={[
									"the right to access your personal data",
									"the right to correct inaccurate data",
									"the right to erasure",
									"the right to restrict processing",
									"the right to object",
									"the right to data portability",
									"the right to withdraw consent",
									"rights relating to automated decision-making",
								]}
							/>

							<p>
								To exercise your rights, contact us at <EmailLink />.
							</p>

							<p>
								You also have the right to complain to the Information
								Commissioner's Office ("ICO"):{" "}
								<a href="https://ico.org.uk">https://ico.org.uk</a>
							</p>

							<h2>Third-Party Websites</h2>

							<p>
								Our website may contain links to third-party websites. We are
								not responsible for the privacy practices or content of those
								websites.
							</p>

							<h2>Changes to This Policy</h2>

							<p>
								We may update this Privacy Policy and Cookie Policy from time to
								time to reflect changes in legal requirements, technology, our
								services, or the way we process personal data and use cookies.
							</p>

							<p>
								Updated versions will be published on our website with the
								revised "Last updated" date. Where changes are material, we may
								notify you directly by email, website notice, or other
								appropriate communication methods.
							</p>

							<p>
								We encourage users to review this policy periodically to stay
								informed about how we protect and process personal data and
								manage cookie preferences.
							</p>

							<h2>Contact Us</h2>

							<p>
								If you have any questions about this Privacy Policy or how we
								process your personal data, please contact:
							</p>

							<p>
								<strong>Email:</strong> <EmailLink />
								<br />
								<strong>Phone:</strong> 020 3970 9900
							</p>

							<p>
								Age Abode Limited
								<br />
								Shoreditch Exchange
								<br />
								Senna Building
								<br />
								Gorsuch Place
								<br />
								London
								<br />
								E2 8JF
							</p>

							<div className="privacy-policy__version-log">
								<h2>Version Log</h2>

								<p>
									<strong>Version 2.0</strong> - May 2026
								</p>

								<p>
									<strong>Previous version:</strong>{" "}
									<a href="/privacy-version-1-0/">
										Version 1.0 - 8th June 2018
									</a>
								</p>
							</div>
						</div>
					</section>
				</main>
				<Footer includeCTA={false} />
			</div>
		</>
	);
}
